/**
 * Level 4, Lessons 1–7 (claude/level-4-source, skills-map level-4.md): place value to hundred thousands, multi-digit
 * adding and subtracting across empty rooms, multiplying (written method and the area puzzle), long division with
 * the leftover written as a fraction (never "R"), rectangles / factors / primes, multiples / LCM / GCF /
 * "times as many", and the secret-rule game (function tables and patterns).
 */
import { int, normalize, pick } from "../core";
import type { Item, Skill } from "../types";
import { addItem, subItem } from "./columns";

const fmt = (n: number) => n.toLocaleString("en-US");
const PLACES = ["ones", "tens", "hundreds", "thousands", "ten thousands", "hundred thousands"];

/* ---------- L4.1 Place value ---------- */
/** A number with at least one empty room (zero) that is not the first digit. */
function withZero(rng: () => number, digits: number): number {
  for (;;) {
    const d = Array.from({ length: digits }, (_, i) => (i === 0 ? int(rng, 1, 9) : rng() < 0.3 ? 0 : int(rng, 1, 9)));
    if (d.slice(1).includes(0)) return Number(d.join(""));
  }
}
const expanded = (n: number, keepZeros = false) => {
  const s = String(n);
  return s.split("").map((d, i) => ({ d: Number(d), v: Number(d) * 10 ** (s.length - 1 - i) })).filter((x) => keepZeros || x.d !== 0).map((x) => String(x.v)).join(" + ");
};

export function pvValueItem(n: number, pos: number): Item {
  const s = String(n), d = Number(s[pos]), place = s.length - 1 - pos, val = d * 10 ** place;
  const shown = fmt(n).split("");
  // underline the digit at `pos` (skip commas)
  let k = -1;
  const html = shown.map((ch) => (/\d/.test(ch) && ++k === pos ? `<u><b>${ch}</b></u>` : ch)).join("");
  return {
    id: `pv-underlined-value-6:${n}:${pos}`,
    skill: "pv-underlined-value-6",
    prompt: `What is the underlined digit worth? <span class="big">${html}</span>`,
    speak: `In ${fmt(n)}, what is the ${d} in the ${PLACES[place]} place worth?`,
    input: { kind: "number" },
    answer: String(val),
    diagnose: (raw) => {
      const r = Number(raw.replace(/,/g, "").trim());
      if (r === d && val !== d) return { code: "pv-digit-only", hint: "That's the digit. What is it worth in its place?" };
      if (place > 0 && (r === val * 10 || r === val / 10)) return { code: "pv-zeros", hint: "Count the digits after it — write that many zeros after the digit." };
      return null;
    },
    hints: ["Which room is it in? Read the places from the right: ones, tens, hundreds, thousands…", `Count the digits after it: ${place}. Write that many zeros after the ${d}.`, `${d} followed by ${place} zero${place === 1 ? "" : "s"}: ${fmt(val)}.`],
  };
}
export const pvValue6: Skill = {
  id: "pv-underlined-value-6",
  name: "Value of a digit, up to hundred thousands",
  gen: (rng) => { const n = int(rng, 10000, 999999), s = String(n); let p = int(rng, 0, s.length - 2); if (s[p] === "0") p = 0; return pvValueItem(n, p); },
};

export function pvExpandedItem(n: number): Item {
  const ans = expanded(n), withZeros = expanded(n, true);
  return {
    id: `pv-expanded-6:${n}`,
    skill: "pv-expanded-6",
    prompt: `Take it apart: write <b>${fmt(n)}</b> in expanded form.`,
    speak: `Write ${fmt(n)} in expanded form.`,
    input: { kind: "text", label: "Expanded form (use +)" },
    answer: ans,
    alsoAccept: [withZeros],
    diagnose: (raw) => {
      if ([ans, withZeros].some((a) => normalize(a) === normalize(raw))) return null;
      const parts = raw.replace(/,/g, "").split("+").map((x) => x.trim()).filter(Boolean);
      const digits = String(n).split("").filter((d) => d !== "0");
      if (parts.length === digits.length && parts.every((p, i) => p === digits[i])) return { code: "pv-digit-only", hint: "Each part is what the digit is WORTH — 300,000, not 3." };
      if (parts.length && parts.join("") === String(n)) return { code: "pv-digit-only", hint: "Each part is what a digit is worth in its place." };
      return null;
    },
    hints: ["What is every digit worth? Start with the first one.", "A zero is an empty room — it adds nothing, so you don't have to write it.", `${ans}.`],
  };
}
export const pvExpanded6: Skill = { id: "pv-expanded-6", name: "Expanded form, up to hundred thousands", gen: (rng) => pvExpandedItem(withZero(rng, pick(rng, [5, 6]))) };

export function pvStandardItem(n: number): Item {
  const ex = expanded(n).split(" + ").map((x) => fmt(Number(x))).join(" + ");
  return {
    id: `pv-standard-6:${n}`,
    skill: "pv-standard-6",
    prompt: `Put it back together: <b>${ex}</b> = ?`,
    input: { kind: "number" },
    answer: String(n),
    diagnose: (raw) => {
      const r = raw.replace(/,/g, "").trim(), nz = String(n).replace(/0/g, "");
      if (r === nz && nz !== String(n)) return { code: "pv-empty-room", hint: "The empty rooms collapsed. A zero holds nothing — but the room still counts." };
      if (r.length < String(n).length && r !== nz) return { code: "pv-empty-room", hint: "Count the rooms: the biggest part tells how many digits the number has. Fill every empty room with a zero." };
      return null;
    },
    hints: [`How many digits does ${ex.split(" + ")[0]} have? That's how many rooms.`, "Put each digit in its room. A room nobody mentions gets a zero.", `${fmt(n)}.`],
  };
}
export const pvStandard6: Skill = { id: "pv-standard-6", name: "Standard form from expanded form", gen: (rng) => pvStandardItem(withZero(rng, pick(rng, [5, 6]))) };

/* ---------- L4.2 Adding and subtracting, multi-digit, across empty rooms ---------- */
export const add4: Skill = {
  id: "add-multidigit-trade",
  name: "Add 4- and 5-digit numbers — trade every time a place fills past 9",
  gen: (rng, level) => {
    let a = 0, b = 0;
    for (;;) {
      a = level >= 2 && rng() < 0.3 ? int(rng, 12000, 48000) : int(rng, 2100, 6900); b = int(rng, 2100, 5999);
      let trades = 0, c = 0;
      for (let x = a, y = b; x || y; x = Math.floor(x / 10), y = Math.floor(y / 10)) { const s = (x % 10) + (y % 10) + c; c = s >= 10 ? 1 : 0; trades += c; }
      if (trades >= (level < 1 ? 1 : 2)) break;
    }
    return { ...addItem(a, b, "add-multidigit-trade"), prompt: "Add. Every time a place fills past 9, make the trade." };
  },
};
export const subZeros: Skill = {
  id: "sub-across-zeros",
  name: "Subtract across empty rooms",
  gen: (rng, level) => {
    const tops = level < 1 ? [3000, 5400, 7200, 6000, 8020] : [3000, 8002, 5400, 7000, 6000, 9050, 4003, 7200, 5004, 6040];
    const a = pick(rng, tops) + (level >= 2 && rng() < 0.3 ? int(rng, 1, 9) * 10000 : 0);
    const b = int(rng, Math.max(1111, Math.floor(a / 5)), a - 300);
    const bb = Number(String(b).split("").map((d) => (d === "0" ? String(int(rng, 1, 9)) : d)).join(""));
    return { ...subItem(a, Math.min(bb, a - 100), "sub-across-zeros"), prompt: "Subtract. Watch for empty rooms — go get a ten from the nearest room that has something in it." };
  },
};

/* ---------- L4.3 Multiplying ---------- */
export function mult2x1Item(a: number, b: number): Item {
  const p = a * b, t = Math.floor(a / 10), o = a % 10;
  return {
    id: `mult-2x1-written:${a}x${b}`,
    skill: "mult-2x1-written",
    prompt: "Multiply with the written method. Ones first.",
    input: { kind: "vertical", top: a, bottom: b, op: "×" },
    speak: `${a} times ${b}.`,
    answer: String(p),
    diagnose: (raw) => {
      const r = Number(raw.trim());
      const noTrade = Number(`${t * b}${(o * b) % 10}`);
      if (r === noTrade && r !== p) return { code: "mult-lost-trade", hint: `The ones made ${o * b} — the ${Math.floor((o * b) / 10)} tens trade next door. Did they get added to the tens?` };
      if (r === t * b + o * b && r !== p) return { code: "mult-place-lost", hint: `The tens digit is worth ${t * 10}. ${b} × ${t * 10} is ${b * t * 10}.` };
      return null;
    },
    hints: [`Ones first: ${b} × ${o} = ${o * b}. Write the ${(o * b) % 10}; trade the ${Math.floor((o * b) / 10)} tens next door.`, `Then the tens: ${b} × ${t} = ${t * b}, plus the ${Math.floor((o * b) / 10)} you traded.`, `${b} × ${t * 10} = ${t * b * 10} and ${b} × ${o} = ${o * b}: ${t * b * 10} + ${o * b} = ${p}.`],
  };
}
export const mult2x1: Skill = { id: "mult-2x1-written", name: "2-digit × 1-digit, the written method", gen: (rng) => { let a = int(rng, 28, 69); while ((a % 10) < 2) a++; return mult2x1Item(a, int(rng, 3, 8)); } };

export function areaModelItem(a: number, b: number, askSplit: boolean): Item {
  const at = Math.floor(a / 10) * 10, ao = a % 10, bt = Math.floor(b / 10) * 10, bo = b % 10;
  const parts = [at * bt, ao * bt, at * bo, ao * bo], p = a * b;
  const grid = `<table class="amodel" aria-label="An area model for ${a} × ${b}"><tr><td></td><th>${askSplit ? "?" : at}</th><th>${askSplit ? "?" : ao}</th></tr><tr><th>${askSplit ? "?" : bt}</th><td>${askSplit ? "" : `${at} × ${bt}`}</td><td>${askSplit ? "" : `${ao} × ${bt}`}</td></tr><tr><th>${askSplit ? "?" : bo}</th><td>${askSplit ? "" : `${at} × ${bo}`}</td><td>${askSplit ? "" : `${ao} × ${bo}`}</td></tr></table>`;
  const f = askSplit
    ? [{ label: `${a} =`, width: 2 }, { label: "+", width: 1 }, { label: `${b} =`, width: 2 }, { label: "+", width: 1 }, { label: "parts:", width: 3 }, { label: "+", width: 3 }, { label: "+", width: 3 }, { label: "+", width: 3 }, { label: "=", width: 3 }]
    : [{ label: `${at} × ${bt} =`, width: 3 }, { label: `${ao} × ${bt} =`, width: 3 }, { label: `${at} × ${bo} =`, width: 3 }, { label: `${ao} × ${bo} =`, width: 3 }, { label: "Add the parts:", width: 3 }];
  const ans = askSplit ? `${at}|${ao}|${bt}|${bo}|${parts.join("|")}|${p}` : `${parts.join("|")}|${p}`;
  return {
    id: `mult-2x2-area-model:${a}x${b}:${askSplit ? 1 : 0}`,
    skill: "mult-2x2-area-model",
    prompt: askSplit ? `The area puzzle: <b>${a} × ${b}</b>. Cut each number where the place value splits, fill in the four parts, then add.` : `The area puzzle: <b>${a} × ${b}</b>. Fill in the four parts, then add.`,
    visual: grid,
    input: { kind: "fields", fields: f },
    answer: ans,
    diagnose: (raw) => {
      const v = raw.split("|").map(Number), tot = v[v.length - 1];
      if (tot === at * bt + ao * bo && tot !== p) return { code: "area-two-parts", hint: "That's only two of the four parts. Every box gets counted." };
      if (tot === (at / 10) * (bt / 10) + ao * bo) return { code: "mult-place-lost", hint: `The tens are worth ${at} and ${bt}, not ${at / 10} and ${bt / 10}.` };
      return null;
    },
    hints: [`Cut where the place value splits: ${a} = ${at} + ${ao}, ${b} = ${bt} + ${bo}.`, `The four parts: ${at} × ${bt}, ${ao} × ${bt}, ${at} × ${bo}, ${ao} × ${bo}.`, `${parts.join(" + ")} = ${p}.`],
  };
}
export const areaModel: Skill = {
  id: "mult-2x2-area-model",
  name: "2-digit × 2-digit — the area puzzle",
  gen: (rng, level) => { const a = int(rng, 1, 3) * 10 + int(rng, 1, 9), b = int(rng, 1, 2) * 10 + int(rng, 1, 9); return areaModelItem(a, b, level >= 1); },
};

/* ---------- L4.4 Long division: the leftover is a fraction waiting to be written ---------- */
export function divLeftoverItem(n: number, d: number): Item {
  const q = Math.floor(n / d), r = n % d;
  return {
    id: `div-fraction-leftover:${n}/${d}`,
    skill: "div-fraction-leftover",
    prompt: `Divide: <b>${n} ÷ ${d}</b>. Write the leftover as a fraction — no "R."`,
    visual: `<div class="divb" aria-hidden="true"><span class="dv">${d}</span><span class="dd">${n}</span></div>`,
    speak: `${n} divided by ${d}. Write the leftover as a fraction.`,
    input: { kind: "fields", fields: [{ width: 2, suffix: "and" }, { width: 1 }, { label: "/", width: 1 }] },
    answer: `${q}|${r}|${d}`,
    diagnose: (raw) => {
      const [a, b, c] = raw.split("|").map(Number);
      if (a === q && b === r && c === q && q !== d) return { code: "div-leftover-bottom", hint: "The bottom of the leftover is the number of groups — the number you divided by." };
      if (a === q && b === d && c === r) return { code: "frac-flipped", hint: "The leftover goes on top; the number of groups on the bottom." };
      if (b !== undefined && b >= d) return { code: "div-leftover-big", hint: `A leftover of ${b} is enough for another round of ${d}. Divide again.` };
      if (a === q + 1 || a === q - 1) return { code: "div-count-slip", hint: `How many ${d}s fit in ${n}? Count by ${d}s — jump to the nearest anchor.` };
      return null;
    },
    hints: [n >= d * 10 ? `Divide, multiply, subtract, bring down. How many ${d}s in ${Math.floor(n / 10)}?` : `How many ${d}s fit in ${n}? Count by ${d}s.`, `${d} × ${q} = ${d * q}. ${n} − ${d * q} leaves ${r}. A leftover is a fraction waiting to be written.`, `${q} and ${r}/${d} — the leftover on top, the number of groups on the bottom.`],
  };
}
export const divLeftover: Skill = {
  id: "div-fraction-leftover",
  name: "Long division — the leftover as a fraction",
  gen: (rng, level) => {
    const d = pick(rng, [4, 5, 6]);
    for (;;) { const n = level < 1 ? int(rng, 17, 41) : int(rng, 58, 95); if (n % d) return divLeftoverItem(n, d); }
  },
};

/* ---------- L4.5 Rectangles, factors and primes ---------- */
export const factorsOf = (n: number) => Array.from({ length: n }, (_, i) => i + 1).filter((k) => n % k === 0);
export function factorsItem(n: number): Item {
  const f = factorsOf(n), pairs = f.filter((k) => k * k <= n).map((k) => `${k} × ${n / k}`);
  return {
    id: `factors-of:${n}`,
    skill: "factors-of",
    prompt: `List <b>all the factors of ${n}</b>, in order. Every rectangle you can build from ${n} squares is a factor pair.`,
    input: { kind: "list", label: "Factors:" },
    answer: f.join(";"),
    diagnose: (raw) => {
      const got = raw.split(";").filter(Boolean).map(Number);
      if (got.length && got.every((g) => n % g === 0) && got.length < f.length) {
        const miss = f.find((k) => !got.includes(k))!;
        return { code: "factor-missed", hint: `Every one of those fits. One rectangle is missing — try a row of ${miss === n || miss === 1 ? "1" : Math.min(miss, n / miss)}.` };
      }
      if (got.some((g) => n % g !== 0)) return { code: "factor-not-fit", hint: "A factor fits: it divides in evenly. Check each one — does it build a rectangle?" };
      if (got.length === f.length && got.join(";") !== f.join(";")) return { code: "factor-order", hint: "All there — now put them in order, smallest first." };
      return null;
    },
    hints: ["Start with 1 × the number — the single row.", "Try rows of 2, 3, 4… Stop when the pairs start repeating.", `The rectangles: ${pairs.join(", ")}. The factors: ${f.join(", ")}.`],
  };
}
export const factorsSkill: Skill = { id: "factors-of", name: "List all the factors", gen: (rng, level) => factorsItem(pick(rng, level < 1 ? [12, 15, 16, 18, 20, 24, 28, 30, 36] : [42, 45, 48, 54, 60, 64, 72, 75, 80, 84, 90, 96, 100])) }; // 4.OA.4: 1–100

export function primeItem(n: number): Item {
  const f = factorsOf(n), ways = f.filter((k) => k * k <= n).length, prime = f.length === 2;
  return {
    id: `prime-composite:${n}`,
    skill: "prime-composite",
    prompt: `Build rectangles from <b>${n}</b> squares. Prime (only one rectangle — a single row) or composite (more than one)?`,
    input: { kind: "choice", options: ["prime", "composite"] },
    answer: prime ? "prime" : "composite",
    diagnose: (raw) => (raw === "prime" && !prime && n % 2 === 1 ? { code: "prime-odd", hint: `Odd isn't the same as prime. Try a row of ${f[1]}.` } : null),
    hints: [`Is there any row besides 1 × ${n}? Try 2, 3, 4…`, prime ? `Nothing fits except 1 and ${n}. How stubborn is ${n}?` : `${f[1]} fits: ${f[1]} × ${n / f[1]!}.`, prime ? `Only 1 × ${n}: prime.` : `${ways} different rectangles: composite.`],
  };
}
export const primeSkill: Skill = { id: "prime-composite", name: "Prime or composite — count the rectangles", gen: (rng, level) => primeItem(int(rng, level < 1 ? 11 : 41, level < 1 ? 40 : 99)) }; // 4.OA.4: 1–100

/* ---------- L4.6 Multiples, LCM, GCF, times as many ---------- */
export function multiplesItem(n: number): Item {
  const m = [1, 2, 3, 4, 5].map((k) => k * n);
  return {
    id: `multiples-first-five:${n}`, skill: "multiples-first-five",
    prompt: `List the <b>first five multiples of ${n}</b>. Count by ${n}s.`,
    input: { kind: "list", label: "Multiples:" }, answer: m.join(";"),
    diagnose: (raw) => (raw.split(";").map(Number).every((x) => n % x === 0) ? { code: "multiples-factors", hint: "Those are factors — they go INTO the number. Multiples count UP from it." } : raw.startsWith("0;") ? { code: "multiples-zero", hint: "Start with the number itself: the first multiple of " + n + " is " + n + "." } : null),
    hints: [`Count by ${n}s on your keypad.`, `${n}, ${2 * n}, …`, `${m.join(", ")}.`],
  };
}
export const multiplesSkill: Skill = { id: "multiples-first-five", name: "First five multiples", gen: (rng) => multiplesItem(int(rng, 3, 9)) };

const gcd = (a: number, b: number): number => (b ? gcd(b, a % b) : a);
export function lcmItem(a: number, b: number): Item {
  const [big, small] = a > b ? [a, b] : [b, a], l = (a * b) / gcd(a, b), list: number[] = [];
  for (let k = big; k <= l; k += big) list.push(k);
  return {
    id: `lcm-count-bigger:${a}&${b}`, skill: "lcm-count-bigger",
    prompt: `Find the <b>LCM of ${a} and ${b}</b>. Count by the bigger. Check against the smaller.`,
    input: { kind: "number" }, answer: String(l),
    diagnose: (raw) => { const r = Number(raw.trim()); if (r === a * b && r !== l) return { code: "lcm-product", hint: `${r} works, but is it the first one? Count by ${big}s and check each against ${small}.` }; if (r === gcd(a, b)) return { code: "lcm-gcf", hint: "That one looks INSIDE (the GCF). The LCM counts UP." }; return null; },
    hints: [`Count by ${big}s: ${big}, ${2 * big}…`, `Check each one: does ${small} go into it?`, `${list.join(", ")} — ${l} is the first one ${small} goes into.`],
  };
}
const LCM_PAIRS: [number, number][] = [[15, 6], [6, 4], [8, 6], [10, 4], [12, 8], [9, 6], [10, 8], [12, 9], [8, 3], [10, 6], [4, 3], [6, 9]];
export const lcmSkill: Skill = { id: "lcm-count-bigger", name: "LCM — count by the bigger", gen: (rng) => { const [a, b] = pick(rng, LCM_PAIRS); return lcmItem(a, b); } };

export function gcfItem(a: number, b: number): Item {
  const g = gcd(a, b), fa = factorsOf(a), fb = factorsOf(b);
  return {
    id: `gcf-list-factors:${a}&${b}`, skill: "gcf-list-factors",
    prompt: `Find the <b>GCF of ${a} and ${b}</b>. List the factors of each, then take the greatest they share.`,
    input: { kind: "number" }, answer: String(g),
    diagnose: (raw) => { const r = Number(raw.trim()); if (r === (a * b) / g) return { code: "lcm-gcf", hint: "That counts UP (the LCM). The GCF looks INSIDE — it's a factor of both." }; if (fa.includes(r) && fb.includes(r) && r < g) return { code: "gcf-not-greatest", hint: "That one is shared — is there a greater one they share?" }; return null; },
    hints: [`Factors of ${a}: ${fa.join(", ")}.`, `Factors of ${b}: ${fb.join(", ")}. Hunt for the shared ones.`, `They share ${fa.filter((x) => fb.includes(x)).join(", ")}; the greatest is ${g}.`],
  };
}
const GCF_PAIRS: [number, number][] = [[20, 30], [12, 18], [8, 20], [9, 12], [12, 20], [16, 24], [15, 25], [18, 27], [14, 21], [24, 30]];
export const gcfSkill: Skill = { id: "gcf-list-factors", name: "GCF — hunt for the shared factors", gen: (rng) => { const [a, b] = pick(rng, GCF_PAIRS); return gcfItem(a, b); } };

export function timesAsManyItem(base: number, k: number, word: boolean): Item {
  const p = base * k;
  const things = ["beads", "markers", "stickers", "cards", "shells"];
  const t = things[(base + k) % things.length]!;
  return word
    ? { id: `times-as-many:w:${base}x${k}`, skill: "times-as-many", prompt: `A jar has <b>${base} ${t}</b>. Another jar has <b>${k} times as many</b>. How many ${t} in the second jar?`, input: { kind: "number" }, answer: String(p),
        diagnose: (raw) => (Number(raw.trim()) === base + k ? { code: "times-as-many-added", hint: "\"Times as many\" is multiplication used to compare." } : null),
        hints: [`Draw a bar of ${base}. The other bar is ${k} of those.`, `${k} copies of ${base}.`, `${k} × ${base} = ${p} ${t}.`] }
    : { id: `times-as-many:f:${p}/${base}`, skill: "times-as-many", prompt: `<b>${p}</b> is how many times as many as <b>${base}</b>?`, input: { kind: "number" }, answer: String(k),
        diagnose: (raw) => (Number(raw.trim()) === p - base ? { code: "times-as-many-added", hint: "Not how many more — how many TIMES. Backward is dividing." } : null),
        hints: [`How many ${base}s make ${p}?`, `Backward is dividing — how many ${base}s are in ${p}?`, `${k} × ${base} = ${p}, so ${k} times as many.`] };
}
export const timesAsMany: Skill = { id: "times-as-many", name: "Times as many — multiplication used to compare", gen: (rng) => timesAsManyItem(int(rng, 4, 8), int(rng, 3, 6), rng() < 0.5) };

/* ---------- L4.7 The secret-rule game ---------- */
type Rule = { name: string; f: (x: number) => number; say: string[] };
const R = {
  times: (k: number): Rule => ({ name: `×${k}`, f: (x) => x * k, say: [`×${k}`, `x${k}`, `times ${k}`, `multiply by ${k}`] }),
  plus: (k: number): Rule => ({ name: `+${k}`, f: (x) => x + k, say: [`+${k}`, `add ${k}`, `plus ${k}`] }),
  minus: (k: number): Rule => ({ name: `−${k}`, f: (x) => x - k, say: [`−${k}`, `-${k}`, `minus ${k}`, `subtract ${k}`] }),
  halve: (): Rule => ({ name: "halve (÷2)", f: (x) => x / 2, say: ["÷2", "/2", "halve", "half", "divide by 2"] }),
  double: (): Rule => ({ name: "double (×2)", f: (x) => x * 2, say: ["×2", "x2", "double", "times 2"] }),
};
export function functionTableItem(rule: Rule, ins: number[]): Item {
  const outs = ins.map(rule.f), shown = ins.slice(0, 3), ask = ins.slice(3);
  const table = `<table class="ftable" aria-label="In and Out table"><tr><th>In</th>${ins.map((x) => `<td>${x}</td>`).join("")}</tr><tr><th>Out</th>${outs.map((y, i) => `<td>${i < 3 ? y : "?"}</td>`).join("")}</tr></table>`;
  return {
    id: `function-table:${rule.name}:${ins.join(",")}`, skill: "function-table",
    prompt: "The secret-rule game. Find the rule — it has to work on EVERY row — then fill in the missing Outs.",
    visual: table,
    input: { kind: "fields", fields: [{ label: "Rule:", width: 3, text: true }, { label: `In ${ask[0]} →`, width: 3 }, { label: `In ${ask[1]} →`, width: 3 }] },
    answer: `${rule.say[0]}|${outs[3]}|${outs[4]}`,
    alsoAccept: rule.say.slice(1).map((s) => `${s}|${outs[3]}|${outs[4]}`),
    diagnose: (raw) => {
      const [r] = raw.split("|");
      const d = outs[0]! - shown[0]!;
      if (r && (r.trim() === `+${d}` || r.trim() === `add ${d}`) && rule.f(shown[1]!) !== shown[1]! + d) return { code: "rule-one-row", hint: `+${d} works on the first row. Check it against EVERY row.` };
      return null;
    },
    hints: ["Feed in a number, look at what comes out. Is it bigger? Much bigger? Smaller?", `Try your rule on every row: ${shown.map((x, i) => `${x} → ${outs[i]}`).join(", ")}.`, `The rule is ${rule.name}: ${ask.map((x, i) => `${x} → ${outs[3 + i]}`).join(", ")}.`],
  };
}
export const functionTable: Skill = {
  id: "function-table", name: "Function tables — find the secret rule",
  gen: (rng) => {
    const kind = int(rng, 0, 2);
    if (kind === 2) { const ins = [10, 8, 6, 4, 2].map((x) => x + 2 * int(rng, 0, 2)); return functionTableItem(R.halve(), ins); }
    const rule = kind === 0 ? R.times(int(rng, 2, 7)) : R.plus(pick(rng, [2, 4, 5, 9]));
    const start = int(rng, 1, 4), step = int(rng, 1, 2);
    return functionTableItem(rule, [0, 1, 2, 3, 4].map((i) => start + i * step));
  },
};
export function patternItem(rule: Rule, first: number): Item {
  const t = [first]; for (let i = 0; i < 5; i++) t.push(rule.f(t[i]!));
  return {
    id: `pattern-next-two:${rule.name}:${first}`, skill: "pattern-next-two",
    prompt: `Find the rule, then write the next two numbers: <b>${t.slice(0, 4).join(", ")}, ___, ___</b>`,
    input: { kind: "fields", fields: [{ label: "Rule:", width: 3, text: true }, { label: "next", width: 3 }, { label: "then", width: 3 }] },
    answer: `${rule.say[0]}|${t[4]}|${t[5]}`,
    alsoAccept: rule.say.slice(1).map((s) => `${s}|${t[4]}|${t[5]}`),
    diagnose: (raw) => { const [r] = raw.split("|"); if (rule.name.startsWith("double") && r && /^\+/.test(r.trim())) return { code: "rule-one-row", hint: "Check that rule against EVERY step — does it carry you from each number to the next?" }; return null; },
    hints: ["What carries you from each number to the next? Is it growing or shrinking?", `${t[0]} → ${t[1]} → ${t[2]}: check the step every time.`, `The rule is ${rule.name}: ${t[4]}, ${t[5]}.`],
  };
}
export const patternSkill: Skill = {
  id: "pattern-next-two", name: "Number patterns — find the rule, next two",
  gen: (rng) => {
    const kind = int(rng, 0, 2);
    if (kind === 0) { const k = pick(rng, [3, 6, 8, 9]); return patternItem(R.minus(k), int(rng, 6 * k, 90)); }
    if (kind === 1) return patternItem(R.plus(pick(rng, [4, 5, 7, 8])), int(rng, 2, 30));
    return patternItem(R.double(), pick(rng, [2, 3, 4, 5]));
  },
};

export const LEVEL4_L1TO7_SKILLS: Skill[] = [pvValue6, pvExpanded6, pvStandard6, add4, subZeros, mult2x1, areaModel, divLeftover, factorsSkill, primeSkill, multiplesSkill, lcmSkill, gcfSkill, timesAsMany, functionTable, patternSkill];
